import { useMemo, type ElementType } from "react";
import { cn } from "@/lib/utils";

const ALLOWED_TAGS = new Set([
  "STRONG",
  "B",
  "EM",
  "I",
  "U",
  "BR",
  "P",
  "UL",
  "OL",
  "LI",
  "A",
  "H1",
  "H2",
  "H3",
]);

/** Detects markup that's already real HTML (from the API) vs. plain markdown/chat text. */
const HAS_BLOCK_HTML = /<\/?(p|div|ul|ol|li|h[1-6])\b/i;

/**
 * Converts lightweight markdown (`**bold**`) into real HTML before sanitizing.
 * Bare newlines are only turned into `<br />` for plain-text content (e.g. chatbot
 * transcript turns) — real HTML from the API already uses block tags for spacing,
 * so converting its whitespace too would add unwanted extra line breaks.
 */
function convertMarkdown(input: string): string {
  const withBold = input.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  if (HAS_BLOCK_HTML.test(input)) return withBold;
  return withBold.replace(/\n/g, "<br />");
}

/**
 * Default rich-text styling applied to all rendered content, since Tailwind's
 * preflight strips native list/heading/paragraph styling — without this, `<ul>`,
 * `<h1>`-`<h3>` and `<p>` all render as indistinguishable, unspaced plain text.
 */
const DEFAULT_PROSE_CLASSES =
  "[&_p]:mb-2 [&_p]:last:mb-0 [&_ul]:my-1 [&_ul]:list-outside [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-1 [&_ol]:list-outside [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-0.5 [&_li]:pl-0.5 [&_h1]:mb-1 [&_h1]:mt-1.5 [&_h1]:font-semibold [&_h1]:first:mt-0 [&_h2]:mb-1 [&_h2]:mt-1.5 [&_h2]:font-semibold [&_h2]:first:mt-0 [&_h3]:mb-1 [&_h3]:mt-1 [&_h3]:font-semibold [&_h3]:first:mt-0 [&_strong]:font-semibold";

function unwrapElement(el: Element) {
  const parent = el.parentNode;
  if (!parent) return;
  while (el.firstChild) parent.insertBefore(el.firstChild, el);
  el.remove();
}

/**
 * Some API responses contain stray `<li>` elements with no enclosing `<ul>`/`<ol>`
 * (the opening list tag gets dropped upstream). Without a real list container,
 * `<li>` gets no left indentation from our list styling, so the bullet renders
 * flush against the edge. Wrap any orphaned, consecutive `<li>` runs in a
 * synthetic `<ul>` so they get proper list markup and indentation.
 */
function wrapOrphanListItems(container: Element): void {
  const isListContainer = container.tagName === "UL" || container.tagName === "OL";
  if (!isListContainer) {
    const children = Array.from(container.childNodes);
    let i = 0;
    while (i < children.length) {
      const node = children[i];
      if (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === "LI") {
        const wrapper = container.ownerDocument!.createElement("ul");
        container.insertBefore(wrapper, node);
        while (i < children.length) {
          const current = children[i];
          const isLi = current.nodeType === Node.ELEMENT_NODE && (current as Element).tagName === "LI";
          const isBlankText = current.nodeType === Node.TEXT_NODE && !(current.textContent ?? "").trim();
          if (isLi) {
            wrapper.appendChild(current);
            i++;
            continue;
          }
          if (isBlankText) {
            current.parentNode?.removeChild(current);
            i++;
            continue;
          }
          break;
        }
        continue;
      }
      i++;
    }
  }

  Array.from(container.children).forEach((child) => wrapOrphanListItems(child));
}

function isSafeHref(href: string): boolean {
  const value = href.trim().toLowerCase();
  return (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("mailto:") ||
    value.startsWith("tel:") ||
    value.startsWith("/") ||
    value.startsWith("#")
  );
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function sanitizeHtml(input: string): string {
  if (!input) return "";
  const withMarkdown = convertMarkdown(input);
  if (typeof DOMParser === "undefined") return escapeHtml(withMarkdown);
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${withMarkdown}</div>`, "text/html");
  const root = doc.body.firstElementChild;
  if (!root) return "";

  const sanitizeAnchorAttribute = (element: Element, attr: Attr): void => {
    if (attr.name === "href") {
      if (!isSafeHref(attr.value)) {
        element.removeAttribute("href");
      }
      return;
    }
    if (attr.name === "target") {
      if (attr.value === "_blank") {
        element.setAttribute("rel", "noopener noreferrer");
      } else {
        element.removeAttribute("target");
      }
      return;
    }
    if (attr.name === "rel") return;
    element.removeAttribute(attr.name);
  };

  const sanitizeAttributes = (element: Element): void => {
    const attributes = Array.from(element.attributes);
    for (const attr of attributes) {
      if (element.tagName === "A") {
        sanitizeAnchorAttribute(element, attr);
      } else {
        element.removeAttribute(attr.name);
      }
    }
  };

  const traverse = (node: Node) => {
    const children = Array.from(node.childNodes);
    for (const child of children) {
      if (child.nodeType === Node.COMMENT_NODE) {
        child.remove();
        continue;
      }

      if (child.nodeType !== Node.ELEMENT_NODE) continue;

      const element = child as Element;
      traverse(element);

      if (!ALLOWED_TAGS.has(element.tagName)) {
        unwrapElement(element);
        continue;
      }

      sanitizeAttributes(element);
    }
  };

  traverse(root);
  wrapOrphanListItems(root);
  return root.innerHTML;
}

interface FormattedHtmlTextProps {
  html: string | null | undefined;
  className?: string;
  as?: "div" | "p" | "span";
}

export function FormattedHtmlText({ html, className, as = "div" }: Readonly<FormattedHtmlTextProps>) {
  const safeHtml = useMemo(() => sanitizeHtml(html ?? ""), [html]);
  const Tag = as as ElementType;

  return (
    <Tag
      className={cn(DEFAULT_PROSE_CLASSES, className)}
      dangerouslySetInnerHTML={{ __html: safeHtml }}
    />
  );
}
